/**
 * Projects API service — business taxonomy and repository projects
 * (backend functions/projects_api, enterprise-project-organization).
 *
 * Selector types come from @systemdox/types (the frozen projectContext
 * contract); this service adds the admin-editor shapes.
 */

import axios from 'axios';
import type { ProjectScope, SelectorResponse } from '@systemdox/types';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

export interface TaxonomyValue {
  dimKey: string;
  valueSlug: string;
  label: string;
  description?: string;
  archivedAt: string | null;
}

export interface TaxonomyDimension {
  dimKey: string;
  label: string;
  builtin: boolean;
  order: number;
  archivedAt: string | null;
  values: TaxonomyValue[];
}

export interface ProjectRuleClause {
  dim: string;
  in: string[];
}

/** all = every clause required; any = at least one; none = exclusions. */
export interface ProjectRule {
  all?: ProjectRuleClause[];
  any?: ProjectRuleClause[];
  none?: ProjectRuleClause[];
}

export interface Project {
  projectId: string;
  slug: string;
  name: string;
  description: string;
  spaceId: string;
  rule: ProjectRule | null;
  pinnedRepos: string[];
  repoCount: number;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectMember {
  repoFullName: string;
  via: 'pin' | 'rule';
}

export interface ProjectInput {
  name?: string;
  slug?: string;
  description?: string;
  spaceId?: string;
  rule?: ProjectRule | null;
  pinnedRepos?: string[];
  archived?: boolean;
}

export const projectsApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  /** Dimensions + values; the three built-ins bootstrap lazily server-side. */
  async getTaxonomy(): Promise<TaxonomyDimension[]> {
    const { data } = await api.get('/taxonomy');
    return data.dimensions ?? [];
  },

  async createDimension(input: { dimKey: string; label: string }): Promise<TaxonomyDimension> {
    const { data } = await api.post('/taxonomy/dimensions', input);
    return data;
  },

  async updateDimension(
    dimKey: string,
    changes: { label?: string; archived?: boolean },
  ): Promise<TaxonomyDimension> {
    const { data } = await api.patch(`/taxonomy/dimensions/${dimKey}`, changes);
    return data;
  },

  async createValue(
    dimKey: string,
    input: { valueSlug: string; label?: string; description?: string },
  ): Promise<TaxonomyValue> {
    const { data } = await api.post(`/taxonomy/dimensions/${dimKey}/values`, input);
    return data;
  },

  async updateValue(
    dimKey: string,
    valueSlug: string,
    changes: { label?: string; description?: string; archived?: boolean },
  ): Promise<TaxonomyValue> {
    const { data } = await api.patch(`/taxonomy/dimensions/${dimKey}/values/${valueSlug}`, changes);
    return data;
  },

  /**
   * Project listing. Defaults to the `read` plane — projects in spaces the
   * caller can actually read — which is what the header selector needs, since
   * selecting a project filters every surface to its repos.
   *
   * Admin screens that manage projects must pass `'manage'` explicitly: that
   * plane also includes non-sealed spaces an admin holds no content grant on.
   *
   * `space` (the scope selector's cascade) narrows the listing to one space —
   * intersected server-side with the caller's visible spaces, so an unknown or
   * non-visible space just returns an empty list.
   */
  async listProjects(
    q?: string,
    scope: ProjectScope = 'read',
    space?: string,
  ): Promise<SelectorResponse> {
    const params: Record<string, string> = { scope };
    if (q) params.q = q;
    if (space) params.space = space;
    const { data } = await api.get('/projects', { params });
    return data;
  },

  async createProject(input: ProjectInput): Promise<Project> {
    const { data } = await api.post('/projects', input);
    return data;
  },

  /** `ref` is a projectId or slug — the API resolves both. */
  async getProject(ref: string): Promise<Project> {
    const { data } = await api.get(`/projects/${ref}`);
    return data;
  },

  async updateProject(ref: string, changes: ProjectInput): Promise<Project> {
    const { data } = await api.patch(`/projects/${ref}`, changes);
    return data;
  },

  async listProjectRepos(ref: string): Promise<{ membershipVersion: number; repos: ProjectMember[] }> {
    const { data } = await api.get(`/projects/${ref}/repos`);
    return data;
  },

  /** Editor dry-run: what a rule + pins would match, without saving. */
  async previewMembership(input: {
    rule?: ProjectRule | null;
    pinnedRepos?: string[];
  }): Promise<{ total: number; repos: ProjectMember[] }> {
    const { data } = await api.post('/projects/preview', input);
    return data;
  },
};
